// v3.18-1374 EQF-INTAKE-ALIAS-UI — Company Settings now hosts an
// "Intake Aliases" tab so ops can manage the alias table per company
// without a separate admin surface. Writes go through the SSOT resolver
// at services/company_alias_resolver.py via /api/companies/:id/aliases.
//
// v3.15-1212 ADMIN-008 — Company Settings.
//
// Per-company configuration surface: general profile, branding,
// operational settings, and feature flags. All updates append audit
// rows. No business logic.
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Settings, Building2, Palette, Flag, History, Upload, Save, Tags, Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const TIME_ZONES = [
  'America/New_York', 'America/Chicago', 'America/Denver', 'America/Los_Angeles',
  'America/Phoenix', 'America/Anchorage', 'Pacific/Honolulu', 'Europe/London', 'UTC',
];
const CURRENCIES = ['USD', 'CAD', 'EUR', 'GBP', 'AUD', 'JPY'];

const fmtDate = (iso) => (iso ? iso.slice(0, 19).replace('T', ' ') : '—');

/* ============================== Page ============================== */

export default function CompanySettingsPage() {
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState('');
  const [settings, setSettings]   = useState(null);
  const [audit, setAudit]         = useState([]);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState(null);
  const [tab, setTab]             = useState('general');

  // Local mutable form state (mirrors settings)
  const [general, setGeneral]         = useState({});
  const [branding, setBranding]       = useState({});
  const [operational, setOperational] = useState({});
  const [flags, setFlags]             = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/companies');
        const list = data?.items || data || [];
        setCompanies(list);
        if (list.length > 0) setCompanyId(list[0].id);
      } catch { /* non-fatal */ }
    })();
  }, []);

  const fetchAll = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError(null);
    try {
      const [s, a] = await Promise.all([
        api.get(`/company-settings/${companyId}`),
        api.get(`/company-settings/${companyId}/audit`).catch(() => ({ data: { items: [] } })),
      ]);
      setSettings(s.data.item);
      setGeneral(s.data.item.general || {});
      setBranding(s.data.item.branding || {});
      setOperational(s.data.item.operational || {});
      setFlags(s.data.item.feature_flags || {});
      setAudit(a.data?.items || []);
    } catch (e) {
      if (e?.response?.status === 403) setError('Settings restricted for your role');
      else toast.error('Failed to load settings');
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const saveSection = async (section) => {
    setSaving(true);
    try {
      const body = section === 'general'      ? { general }
                 : section === 'branding'     ? { branding }
                 : section === 'operational'  ? { operational }
                 : section === 'feature_flags' ? { feature_flags: flags }
                 : {};
      const { data } = await api.put(`/company-settings/${companyId}`, body);
      setSettings(data.item);
      toast.success(`Saved · v${data.version}`);
      const a = await api.get(`/company-settings/${companyId}/audit`);
      setAudit(a.data?.items || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/company-settings/${companyId}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo uploaded'); fetchAll();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setSaving(false); e.target.value = ''; }
  };

  const logoUrl = settings?.branding?.logo_content_type
    ? `${api.defaults.baseURL}/company-settings/${companyId}/logo?ts=${settings.version}`
    : null;

  return (
    <div className="space-y-4" data-testid="company-settings-page">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Settings className="w-6 h-6 text-slate-600" />
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">Company Settings</h1>
            <p className="text-sm text-slate-500">
              Configuration-only surface. Every save appends an audit row. No business calculations.
            </p>
          </div>
        </div>
        <select data-testid="cs-company-select" value={companyId} onChange={(e) => setCompanyId(e.target.value)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm min-w-[260px]">
          {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {error ? (
        <div className="p-6 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl" data-testid="cs-error">{error}</div>
      ) : loading || !settings ? (
        <div className="p-10 flex items-center justify-center text-slate-500 bg-white rounded-2xl border">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading settings…
        </div>
      ) : (
        <>
          <div className="flex items-center gap-1 border-b border-slate-200">
            {[
              { k: 'general', label: 'General', icon: <Building2 className="w-4 h-4" /> },
              { k: 'branding', label: 'Branding', icon: <Palette className="w-4 h-4" /> },
              { k: 'operational', label: 'Operational', icon: <Settings className="w-4 h-4" /> },
              { k: 'flags', label: 'Feature Flags', icon: <Flag className="w-4 h-4" /> },
              { k: 'aliases', label: 'Intake Aliases', icon: <Tags className="w-4 h-4" /> },
              { k: 'audit', label: 'Audit', icon: <History className="w-4 h-4" /> },
            ].map((t) => (
              <button key={t.k} data-testid={`cs-tab-${t.k}`} onClick={() => setTab(t.k)}
                      className={
                        'px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition flex items-center gap-1.5 ' +
                        (tab === t.k ? 'border-slate-800 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-800')
                      }>
                {t.icon}{t.label}
              </button>
            ))}
            <div className="ml-auto text-xs text-slate-500 pr-2">v{settings.version} · updated {fmtDate(settings.updated_at)}</div>
          </div>

          {/* GENERAL */}
          {tab === 'general' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4" data-testid="cs-general">
              <div className="grid grid-cols-2 gap-3">
                {[
                  ['company_name', 'Company Name'],
                  ['display_name', 'Display Name'],
                  ['company_code', 'Company Code'],
                  ['status', 'Status'],
                  ['contact_email', 'Contact Email'],
                  ['contact_phone', 'Contact Phone'],
                  ['address_line1', 'Address Line 1'],
                  ['address_line2', 'Address Line 2'],
                  ['city', 'City'],
                  ['state', 'State/Region'],
                  ['postal_code', 'Postal Code'],
                  ['country', 'Country'],
                ].map(([k, label]) => (
                  <div key={k}>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</label>
                    <Input data-testid={`cs-general-${k}`} value={general[k] || ''}
                           onChange={(e) => setGeneral({ ...general, [k]: e.target.value })} />
                  </div>
                ))}
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Time Zone</label>
                  <select data-testid="cs-general-time_zone" value={general.time_zone || ''}
                          onChange={(e) => setGeneral({ ...general, time_zone: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    {TIME_ZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Default Currency</label>
                  <select data-testid="cs-general-default_currency" value={general.default_currency || ''}
                          onChange={(e) => setGeneral({ ...general, default_currency: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <Button data-testid="cs-save-general" onClick={() => saveSection('general')} disabled={saving}
                      className="bg-slate-800 hover:bg-slate-900 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save General
              </Button>
            </div>
          )}

          {/* BRANDING */}
          {tab === 'branding' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4" data-testid="cs-branding">
              <div className="flex items-start gap-6">
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Current Logo</div>
                  <div className="w-32 h-32 rounded-2xl border border-slate-200 flex items-center justify-center overflow-hidden bg-slate-50" data-testid="cs-logo-preview">
                    {logoUrl ? <img src={logoUrl} alt="Logo" className="max-w-full max-h-full" /> :
                      <span className="text-xs text-slate-400">No logo</span>}
                  </div>
                  <label className="inline-flex items-center gap-2 mt-3 text-sm text-blue-600 hover:text-blue-800 cursor-pointer">
                    <Upload className="w-4 h-4" /> Upload
                    <input type="file" accept="image/*" className="hidden" data-testid="cs-logo-upload" onChange={uploadLogo} />
                  </label>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Accent Color</label>
                    <div className="flex items-center gap-2">
                      <Input data-testid="cs-branding-accent" value={branding.accent_color || ''}
                             onChange={(e) => setBranding({ ...branding, accent_color: e.target.value })}
                             placeholder="#2563eb" />
                      <div className="w-10 h-10 rounded-lg border border-slate-200"
                           style={{ backgroundColor: branding.accent_color || '#ffffff' }} />
                    </div>
                  </div>
                  <div className="col-span-2 space-y-3">
                    {['theme_assets', 'email_branding_assets', 'internal_display_branding'].map((k) => (
                      <div key={k}>
                        <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
                          {k.replaceAll('_', ' ')} (JSON)
                        </label>
                        <textarea data-testid={`cs-branding-${k}`}
                                  value={JSON.stringify(branding[k] || {}, null, 2)}
                                  onChange={(e) => {
                                    try { setBranding({ ...branding, [k]: JSON.parse(e.target.value || '{}') }); }
                                    catch { /* keep raw until valid */ }
                                  }}
                                  rows={3}
                                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-xs font-mono" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <Button data-testid="cs-save-branding" onClick={() => saveSection('branding')} disabled={saving}
                      className="bg-slate-800 hover:bg-slate-900 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Branding
              </Button>
            </div>
          )}

          {/* OPERATIONAL */}
          {tab === 'operational' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4" data-testid="cs-operational">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Fiscal Year Start (Month)</label>
                  <Input data-testid="cs-op-fiscal" type="number" min="1" max="12"
                         value={operational.fiscal_year_start_month ?? 1}
                         onChange={(e) => setOperational({ ...operational, fiscal_year_start_month: Number(e.target.value) || 1 })} />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">Week Start Day</label>
                  <select data-testid="cs-op-week" value={operational.week_start_day || 'monday'}
                          onChange={(e) => setOperational({ ...operational, week_start_day: e.target.value })}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm">
                    {['monday', 'sunday'].map((d) => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <Button data-testid="cs-save-operational" onClick={() => saveSection('operational')} disabled={saving}
                      className="bg-slate-800 hover:bg-slate-900 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Operational
              </Button>
            </div>
          )}

          {/* FLAGS */}
          {tab === 'flags' && (
            <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4" data-testid="cs-flags">
              <p className="text-xs text-slate-500">
                Toggle module availability for this company. Feature flags are read by their respective modules; disabling here does not delete data.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {Object.entries(flags).map(([k, v]) => (
                  <label key={k} className="flex items-center justify-between border border-slate-200 rounded-xl p-3 bg-slate-50/50">
                    <span className="text-sm font-semibold text-slate-800 capitalize">{k.replaceAll('_', ' ')}</span>
                    <label className="inline-flex items-center cursor-pointer">
                      <input data-testid={`cs-flag-${k}`} type="checkbox" checked={!!v}
                             onChange={(e) => setFlags({ ...flags, [k]: e.target.checked })}
                             className="sr-only peer" />
                      <div className="w-11 h-6 bg-slate-300 rounded-full peer-checked:bg-emerald-500 relative transition">
                        <div className={
                          'absolute w-5 h-5 bg-white rounded-full top-0.5 left-0.5 transition ' +
                          (v ? 'translate-x-5' : '')
                        } />
                      </div>
                    </label>
                  </label>
                ))}
              </div>
              <Button data-testid="cs-save-flags" onClick={() => saveSection('feature_flags')} disabled={saving}
                      className="bg-slate-800 hover:bg-slate-900 text-white">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Feature Flags
              </Button>
            </div>
          )}

          {/* INTAKE ALIASES (v3.18-1374) */}
          {tab === 'aliases' && (
            <IntakeAliasesSection companyId={companyId} />
          )}

          {/* AUDIT */}
          {tab === 'audit' && (
            <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-x-auto" data-testid="cs-audit">
              {audit.length === 0 ? (
                <div className="p-10 text-center text-slate-500">No audit rows yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
                    <tr>
                      <th className="text-left px-3 py-2">Changed</th>
                      <th className="text-left px-3 py-2">Section</th>
                      <th className="text-left px-3 py-2">By</th>
                      <th className="text-left px-3 py-2">Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audit.map((r) => (
                      <tr key={r.id} data-testid={`cs-audit-${r.id}`} className="border-t border-slate-100 align-top">
                        <td className="px-3 py-2 whitespace-nowrap text-xs">{fmtDate(r.changed_at)}</td>
                        <td className="px-3 py-2 text-xs font-mono">{r.section}</td>
                        <td className="px-3 py-2 text-xs">{r.changed_by || '—'}</td>
                        <td className="px-3 py-2 text-xs font-mono max-w-md whitespace-pre-wrap break-words">
                          <div className="text-rose-700 line-through">{JSON.stringify(r.before).slice(0, 120)}</div>
                          <div className="text-emerald-700">{JSON.stringify(r.after).slice(0, 120)}</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ==================== v3.18-1374 Intake Aliases ==================== */

// v3.18-1385 GMAIL-005 — exported so CompanyDetailPage.js can mount it
// as a native tab on `/companies/{id}` without duplicating the API
// wiring.
export function IntakeAliasesSection({ companyId }) {
  const [items, setItems] = useState([]);
  const [canonical, setCanonical] = useState('');
  const [newAlias, setNewAlias] = useState('');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/aliases`);
      setItems(data?.items || []);
      setCanonical(data?.canonical_name || '');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load aliases');
    } finally { setLoading(false); }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const addAlias = async () => {
    const value = (newAlias || '').trim();
    if (!value) { toast.error('Enter an alias first'); return; }
    setAdding(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/aliases`, { alias: value });
      toast.success(`Alias '${data.item.alias}' added`);
      setNewAlias('');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally { setAdding(false); }
  };

  const removeAlias = async (row) => {
    if (!window.confirm(`Remove alias '${row.alias}'?`)) return;
    setRemovingId(row.id);
    try {
      await api.delete(`/companies/${companyId}/aliases/${row.id}`);
      toast.success(`Alias '${row.alias}' removed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    } finally { setRemovingId(null); }
  };

  return (
    <div
      className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm space-y-4"
      data-testid="cs-aliases"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-bold text-slate-900">Intake Aliases</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-2xl">
            Rep-company strings that resolve to <span className="font-semibold text-slate-700">{canonical || '—'}</span>{' '}
            when a new intake lands. Matching is case-insensitive + whitespace-collapsed.
            Add every spelling, abbreviation, DBA, or variation your reps type — matching is case-insensitive + whitespace-collapsed.  Examples: <span className="font-mono">ABC</span>,{' '}
            <span className="font-mono">ABC Company</span>, <span className="font-mono">ABC Co.</span>, <span className="font-mono">ABC Services</span>.
          </p>
        </div>
      </div>

      <div className="flex items-end gap-2 max-w-xl">
        <div className="flex-1">
          <label className="block text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            New alias
          </label>
          <Input
            data-testid="cs-alias-input"
            value={newAlias}
            onChange={(e) => setNewAlias(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addAlias(); }}
            placeholder="e.g. ABC or ABC Company"
          />
        </div>
        <Button
          data-testid="cs-alias-add"
          onClick={addAlias}
          disabled={adding || !newAlias.trim()}
          className="bg-slate-800 hover:bg-slate-900 text-white"
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          Add
        </Button>
      </div>

      <div className="border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-6 flex items-center justify-center text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading aliases…
          </div>
        ) : items.length === 0 ? (
          <div
            className="p-6 text-center text-sm text-slate-500"
            data-testid="cs-aliases-empty"
          >
            No aliases yet. Add one above so intake auto-assigns this company.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 uppercase text-[11px] tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">Alias</th>
                <th className="text-left px-3 py-2">Normalized</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Added</th>
                <th className="text-right px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr
                  key={r.id}
                  data-testid={`cs-alias-row-${r.id}`}
                  className="border-t border-slate-100"
                >
                  <td className="px-3 py-2 font-mono text-slate-800">{r.alias}</td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-500">{r.alias_normalized}</td>
                  <td className="px-3 py-2 text-xs">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                      {r.source || 'operator'}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                    {r.created_at ? r.created_at.slice(0, 10) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      data-testid={`cs-alias-remove-${r.id}`}
                      onClick={() => removeAlias(r)}
                      disabled={removingId === r.id}
                      className="inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 text-xs font-semibold disabled:opacity-50"
                    >
                      {removingId === r.id
                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        : <X className="w-3.5 h-3.5" />}
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
